import { Injectable, inject } from '@angular/core'
import { LocaleService } from '@core/locale/locale.service'
import { HttpService } from '@core/request/http.service'
import { type numeric } from '@core/type/numeric'
import { IS_A_FUNCTION, IS_A_STRING, IS_A_STRING_AND_NOT_EMPTY, IS_NOT_SET, IS_NUMERIC, IS_ON, IS_SET } from '@core/util/check.util'
import { TO_NUMBER } from '@core/util/convert.util'
import { FROM_ISO_TO_LOCAL_DTT } from '@core/util/display.util'
import { kBackendPtOverlay } from '@core/locale/pt-overlay'
import { DateTime, type Duration } from 'luxon'
import { BehaviorSubject } from 'rxjs'

export interface DictionaryEntry {
  [b: string]: string
  default: string
}

export type DictionaryEntryFactory = (params?: any) => DictionaryEntry

export type DictionaryEntries = Record<string, DictionaryEntry | DictionaryEntryFactory>

export type DictionaryEntriesFactory = (params?: any) => DictionaryEntries

export interface GetLocalizedStringParams {
  fromEntry?: DictionaryEntry | DictionaryEntryFactory
  k?: string
  params?: any
  fromEntries?: DictionaryEntries | DictionaryEntriesFactory
}

@Injectable({ providedIn: 'root' })
export class DictionaryService {
  HttpService = inject(HttpService)
  LocaleService = inject(LocaleService)

  localeBS = new BehaviorSubject(LocaleService.defaultLocale)

  entries: any = {}

  // entries that cannot be simply sorted by line
  // long entries with the same key as entries will overwrite them (see this.initialize logic)
  longEntries = {
    alertResolved_on_dateTimeDisplay: (row: any) => {
      let res: DictionaryEntry = { default: this.get({ k: 'alertStill_active' }) }

      const alResolvedLocalDTT = FROM_ISO_TO_LOCAL_DTT(row.alResolved)

      if (IS_A_STRING_AND_NOT_EMPTY(alResolvedLocalDTT)) {
        res = {
          default: `Resolved on ${alResolvedLocalDTT}`,
          fr: `Résolue le ${alResolvedLocalDTT}`,
          it: `Risolto il ${alResolvedLocalDTT}`,
          es: `Resuelto el ${alResolvedLocalDTT}`
        }
      }

      return res
    },

    durationDisplayParts: (_: { duration: Duration, bAbsolute?: boolean }): string[] => {
      const duration = _.duration

      const d = duration.shiftTo('years', 'months', 'days', 'hours', 'minutes', 'seconds').toObject()

      const bAbsolute = IS_ON(_.bAbsolute)

      const partMap = new Map<string, number>([
        ['year', Math.trunc(TO_NUMBER(d.years))],
        ['month', Math.trunc(TO_NUMBER(d.months))],
        ['day', Math.trunc(TO_NUMBER(d.days))],
        ['hour', Math.trunc(TO_NUMBER(d.hours))],
        ['minute', Math.trunc(TO_NUMBER(d.minutes))],
        ['second', Math.trunc(TO_NUMBER(d.seconds))]
      ])

      const res: string[] = []

      for (const [unit, value] of partMap) {
        if (IS_NUMERIC(value) && (value !== 0)) {
          const valueAbs = Math.abs(value)
          const v = bAbsolute ? valueAbs : value
          const valueDisplay = this.LocaleService.localizeNumber({ value: v })

          const bPlural = valueAbs > 1
          const s = bPlural ? 's' : ''
          const displayUnitKey = `unit${this.LocaleService.toUCF(unit)}${s}`
          const unitDisplay = this.get({ k: displayUnitKey })

          res.push(`${valueDisplay} ${unitDisplay}`)
        }
      }

      return res
    },

    over_N_days: (_: { over_N_days: number }): any => {
      const n = _.over_N_days
      const N = this.LocaleService.localizeNumber({ value: n })

      const bMany = Math.abs(n) > 1
      const bPlural = isNaN(n) || bMany
      const s = bPlural ? 's' : ''

      return {
        default: bPlural ? `Over ${N} day${s}` : 'Over the day',
        fr: bPlural ? `Sur ${N} jour${s}` : 'Sur la journée',
        es: bPlural ? `En ${N} día${s}` : 'En el día',
        it: bPlural ? `Negli ${N} giorno${s}` : 'Nella giornata'
      }
    }
  }

  // set of { key: dictionary }, dictionary => set of { key: entry }, entry => set of { 'default'/locale/lang: string/template }
  extraDictionaries = {

    alertStatusUpdateProcess: (_?: { n?: numeric }) => {
      const n = TO_NUMBER(_?.n)
      const s = (n > 1) ? 's' : ''
      const iE = IS_A_STRING_AND_NOT_EMPTY(s) ? 'i' : 'e'
      const iO = IS_A_STRING_AND_NOT_EMPTY(s) ? 'i' : 'o'
      const N = IS_A_STRING_AND_NOT_EMPTY(s) ? this.LocaleService.localizeNumber({ value: n }) : ''
      return {

        titleTicket: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `Ticket about the ${N} selected alerts?` : 'Ticket about this alert?',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Ticket à propos des ${N} alertes sélectionnées ?` : 'Ticket de cette alerte ?',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Biglietto degli ${N} allarmi selezionati ?` : 'Biglietto di questo allarme ?'
        },
        titleSend: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `Notify about the ${N} selected alerts?` : 'Notify about this alert?',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Notifier à propos des ${N} alertes sélectionnées ?` : 'Notifier de cette alerte ?',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Notifica degli ${N} allarmi selezionati ?` : 'Notifica di questo allarme ?'
        },
        titleReset: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `Reset the ${N} selected alerts?` : 'Reset this alert?',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Remettre à zéro les ${N} alertes sélectionnées ?` : 'Remettre à zéro cette alerte ?',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Ripristina gli ${N} allarmi selezionati ?` : 'Ripristina questo allarme ?'
        },
        titleInProgress: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `Mark the ${N} selected alerts in progress?` : 'Set this alert in progress?',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Les ${N} alertes sélectionnées sont en cours ?` : 'Cette alerte est en cours ?',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Gli ${N} allarmi selezionati sono in corso ?` : 'Questo allarme è stato supportato ?'
        },
        titleResolve: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `Mark the ${N} selected alerts as resolved?` : 'Mark this alert as resolved?',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Marquer les ${N} alertes sélectionnées comme résolues ?` : 'Marquer cette alerte comme résolue ?',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Marchia risoluti gli ${N} allarmi selezionati ?` : 'Marchia risoluto questo allarme ?'
        },
        titleArchive: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `Archive these ${N} alerts?` : 'Archive this alert?',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Archiver les ${N} alertes sélectionnées ?` : 'Archiver cette alerte ?',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Archivia gli ${N} allarmi selezionati ?` : 'Archivia questo allarme ?'
        },
        titleArchiveAll: { default: 'Archive all alerts?', fr: 'Archiver toutes les alertes ?', it: 'Archivia tutti gli allarmi ?' },

        textTicket: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `The ${N} tickets will be created. ` : 'The ticket will be created. ',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Les ${N} tickets vont être créés. ` : 'Le ticket va être créé. ',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Gli ${N} biglietti verranno creati. ` : 'Il biglietto verrà creato. '
        },
        textSend: {
          default: (
            IS_A_STRING_AND_NOT_EMPTY(s)
              ? `The contacts bellow will be notified about these ${N} selected alerts. `
              : 'The contacts bellow will be notified about this alert. '
          ),
          fr: (
            IS_A_STRING_AND_NOT_EMPTY(s)
              ? `Les contacts ci-dessous vont être notifiés des ${N} alertes sélectionnées. `
              : 'Les contacts ci-dessous vont être notifiés de cette alerte. '
          ),
          it: (
            IS_A_STRING_AND_NOT_EMPTY(s)
              ? `I contatti sottostanti verranno avvisati degli ${N} allarmi selezionati. `
              : 'I contatti sottostanti verranno avvisati di questo allarme. '
          )
        },
        textReset: {
          default: IS_A_STRING_AND_NOT_EMPTY(s) ? `The ${N} alerts will be reactivated. ` : 'The alert will be reactivated. ',
          fr: IS_A_STRING_AND_NOT_EMPTY(s) ? `Les ${N} alertes vont être réactivées. ` : 'L\'alerte va être réactivée. ',
          it: IS_A_STRING_AND_NOT_EMPTY(s) ? `Gli ${N} allarmi verranno riattivati. ` : 'L\'allarme verra riattivato. '
        },
        textInProgress: {
          default: (
            IS_A_STRING_AND_NOT_EMPTY(s)
              ? `The ${N} alerts will be marked as "in progress". `
              : 'The alert will be marked as "in progress". '
          ),
          fr: (
            IS_A_STRING_AND_NOT_EMPTY(s)
              ? `Les ${N} alertes vont être marquées comme "en cours". `
              : 'L\'alerte va être marquées comme "en cours". '
          ),
          it: (
            IS_A_STRING_AND_NOT_EMPTY(s)
              ? `Gli ${N} allarmi saranno contrassegnati come "in corso". `
              : 'L\'allarme sarà contrassegnato come "in corso". '
          )
        },
        textResolve: {
          default: 'Some alerts cannot be automatically resolved. Manual status update required. ',
          fr: 'Certaines alertes ne peuvent pas être marquées comme résolues automatiquement. Leur mise à jour manuelle est requise. ',
          it: 'Alcuni allarmi non possono venire marchiati risoluti automaticamente. È necessario l\'aggiornamento manuale del loro stato. '
        },
        textArchive: {
          default: 'Old or no longer relevant alerts can be archived to easily filter them out of the next search results. ',
          fr: `
            Les alertes qui ne sont plus pertinentes ou trop anciennes peuvent être archivées
            pour être écartées facilement des prochains résultats de recherche.
          `,
          it: `
            È possibile archiviare allarmi vecchi o non più pertinenti per filtrarli facilmente
            dai risultati di ricerca successivi.
          `
        },
        textArchiveAll: {
          default: 'All alerts currently in the database will be archived. ',
          fr: 'Toutes les alertes actuellement en base de donnée vont être archivées. ',
          it: 'Tutti gli allarmi attualmente presenti nel database verranno archiviati. '
        },

        doneSent: { default: 'Alert notification sent', fr: 'Notification d\'alerte envoyée', it: 'Notifica di allarme inviata' },
        doneReset: { default: `Alert${s} reset`, fr: `Alerte${s} réactivée${s}`, it: `Allarm${iE} ripristinat${iO}` },
        doneInProgress: { default: `Alert${s} in progress`, fr: `Alerte${s} en cours`, it: `Allarm${iE} in corso` },
        doneResolve: { default: `Alert${s} resolved`, fr: `Alerte${s} résolue${s}`, it: `Allarm${iE} risolut${iO}` },
        doneArchived: { default: `Alert${s} archived`, fr: `Alerte${s} archivée${s}`, it: `Allarm${iE} archiviat${iO}` },
        doneArchivedAll: {
          default: 'All alerts have been archived',
          fr: 'Toutes les alertes ont été archivées',
          it: 'Tutti gli allarmi sono stati archiviati'
        }

      }
    }

  }

  mLastRefreshBS = new BehaviorSubject<DateTime | undefined>(undefined)

  async refresh (_locale?: string): Promise<void> {
    const locale = _locale ?? this.LocaleService.getLocale()
    const urlLocale = encodeURIComponent(locale)
    const { exception, result } = await this.HttpService.get({ path: `/dictionary_entries_translated_locale?locale=${urlLocale}` })
    const entries = (IS_NOT_SET(exception) && IS_SET(result)) ? result : {}
    // merge long entries with entries here (overwrite identical keys)
    this.entries = { ...entries, ...this.longEntries }
    // the backend dictionary has no Portuguese values : overlay the front-end pt map so pt-* locales
    // render Portuguese instead of falling back to the English default (all dictionary-driven pages)
    if (locale.toLowerCase().startsWith('pt')) {
      this.entries = { ...this.entries, ...kBackendPtOverlay }
    }
    // extra dictionaries remain independent
    this.mLastRefreshBS.next(DateTime.now())
  }

  get (_: { fromEntry?: any, k?: string, params?: any, fromEntries?: any }): string {
    let res = ''

    const bK = IS_SET(_?.k)
    const bParams = IS_SET(_?.params)
    const bFromEntry = IS_SET(_?.fromEntry)
    const bFromEntries = IS_SET(_?.fromEntries)

    let entry: any

    if (bFromEntry) {
      entry = IS_A_FUNCTION(_.fromEntry) ? (bParams ? _.fromEntry(_.params) : _.fromEntry()) : _.fromEntry
    } else if (bK) {
      if (bFromEntries) {
        const entries = IS_A_FUNCTION(_.fromEntries) ? (bParams ? _.fromEntries(_.params) : _.fromEntries()) : _.fromEntries
        entry = entries[_.k ?? '!!!']
      } else {
        entry = this.entries[_.k ?? '!!!']
      }
    }

    if (IS_A_STRING(entry)) {
      res = entry
    } else if (IS_SET(entry)) {
      if (IS_A_FUNCTION(entry)) {
        entry = IS_SET(_.params) ? entry(_.params) : entry()
      }
      const locale = this.LocaleService.getLocale()
      const lang = this.LocaleService.getLang()
      const translator: any = entry[locale] ?? entry[lang] ?? entry.default
      res = IS_A_FUNCTION(translator) ? (bParams ? translator(_.params) : translator()) : translator
    }

    return res
  }

  getLC (_: { fromEntry?: any, k?: string, params?: any, fromEntries?: any }): string {
    return this.LocaleService.toLC(this.get(_))
  }

  getUC (_: { fromEntry?: any, k?: string, params?: any, fromEntries?: any }): string {
    return this.LocaleService.toUC(this.get(_))
  }

  getUCF (_: { fromEntry?: any, k?: string, params?: any, fromEntries?: any }): string {
    return this.LocaleService.toUCF(this.get(_))
  }
}
